import type { BalanceItem, BalanceHistoryItem, BalancesResponse, AccountType } from '../types/balances.types';

// Генератор случайных чисел с сидом для консистентности
let seed = 12345;
function random(): number {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
}

function randomBetween(min: number, max: number): number {
    return Math.floor(random() * (max - min + 1)) + min;
}

// Реалистичные названия организаций
const ORGANIZATIONS = [
    'ООО "Сканта-Технологии"',
    'ООО "Софт-Сервис"',
    'ООО "ИнфоПро"',
    'ИП Иванов А.В.',
    'ООО "Цифровые Решения"',
    'ООО "ТехноПрогресс"',
    'ООО "ВебСтор"',
    'ИП Петров С.М.',
    'ООО "МаркетПлюс"',
    'ООО "Девелопмент-Центр"',
];


// Генерация реалистичных сумм
function generateAmount(currency: string): number {
    const multipliers: Record<string, number> = {
        RUB: 1,
        USD: 0.011,
        EUR: 0.01,
        CNY: 0.08,
    };
    
    const base = randomBetween(100000, 50000000);
    const multiplier = multipliers[currency] || 1;
    return Math.round(base * multiplier);
}

// Создание одного счета
function createBalanceItem(
    organization: string,
    accountName: AccountType,
    currency: string,
    offset: number
): BalanceItem {
    const now = new Date();
    now.setMinutes(now.getMinutes() - offset * 5);
    
    return {
        organization,
        accountName,
        amount: generateAmount(currency),
        currency,
        syncedAt: now.toISOString(),
    };
}

// Генерация истории для графиков
export function generateMockHistory(
    baseAmount: number,
    limit: number = 30
): BalanceHistoryItem[] {
    const history: BalanceHistoryItem[] = [];
    const now = new Date();
    
    for (let i = limit - 1; i >= 0; i--) {
        const timestamp = new Date(now.getTime() - i * 5 * 60 * 1000); // каждые 5 мин
        const variation = (random() - 0.5) * baseAmount * 0.1; // ±5% изменение
        const trend = (limit - i) * baseAmount * 0.001; // Небольшой тренд роста
        
        const amount = Math.max(0, baseAmount + variation + trend);
        const prevAmount = history[history.length - 1]?.amount || amount;
        const change = amount - prevAmount;
        
        history.push({
            timestamp: timestamp.toISOString(),
            amount: Math.round(amount),
            change: history.length > 0 ? Math.round(change) : null,
        });
    }
    
    return history;
}

// Генерация полного набора данных
export function generateMockBalances(): BalanceItem[] {
    const balances: BalanceItem[] = [];
    let globalOffset = 0;
    
    ORGANIZATIONS.forEach((org) => {
        // У каждой организации минимум 2 счета (нал и безнал)
        const hasUsd = random() > 0.6;
        const hasEur = random() > 0.8;
        const hasCny = random() > 0.9;
        
        // Рублевые счета
        balances.push(createBalanceItem(org, 'cash', 'RUB', globalOffset++));
        balances.push(createBalanceItem(org, 'noncash', 'RUB', globalOffset++));
        
        if (hasUsd) {
            balances.push(createBalanceItem(org, 'noncash', 'USD', globalOffset++));
        }
        if (hasEur) {
            balances.push(createBalanceItem(org, 'noncash', 'EUR', globalOffset++));
        }
        if (hasCny) {
            balances.push(createBalanceItem(org, 'noncash', 'CNY', globalOffset++));
        }
    });
    
    return balances;
}

// Mock API ответ
export function createMockResponse(): BalancesResponse {
    return {
        data: generateMockBalances(),
        updatedAt: new Date().toISOString(),
        isMock: true,
    } as BalancesResponse;
}

// Имитация задержки сети
export function mockDelay(min = 300, max = 800): Promise<void> {
    const delay = randomBetween(min, max);
    return new Promise(resolve => setTimeout(resolve, delay));
}

// Экспорт статических данных для тестов
export const MOCK_BALANCES_DATA: BalanceItem[] = [
    {
        organization: 'ООО "Сканта-Технологии"',
        accountName: 'cash',
        amount: 1250000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:30:00.000Z',
    },
    {
        organization: 'ООО "Сканта-Технологии"',
        accountName: 'noncash',
        amount: 15800000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:30:00.000Z',
    },
    {
        organization: 'ООО "Сканта-Технологии"',
        accountName: 'noncash',
        amount: 125000,
        currency: 'USD',
        syncedAt: '2026-03-30T21:30:00.000Z',
    },
    {
        organization: 'ООО "Софт-Сервис"',
        accountName: 'cash',
        amount: 450000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:25:00.000Z',
    },
    {
        organization: 'ООО "Софт-Сервис"',
        accountName: 'noncash',
        amount: 8200000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:25:00.000Z',
    },
    {
        organization: 'ИП Иванов А.В.',
        accountName: 'cash',
        amount: 180000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:20:00.000Z',
    },
    {
        organization: 'ИП Иванов А.В.',
        accountName: 'noncash',
        amount: 2500000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:20:00.000Z',
    },
    {
        organization: 'ООО "Цифровые Решения"',
        accountName: 'noncash',
        amount: 45000000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:15:00.000Z',
    },
    {
        organization: 'ООО "ТехноПрогресс"',
        accountName: 'cash',
        amount: 890000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:10:00.000Z',
    },
    {
        organization: 'ООО "ТехноПрогресс"',
        accountName: 'noncash',
        amount: 12300000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:10:00.000Z',
    },
    {
        organization: 'ООО "ТехноПрогресс"',
        accountName: 'noncash',
        amount: 95000,
        currency: 'EUR',
        syncedAt: '2026-03-30T21:10:00.000Z',
    },
    {
        organization: 'ООО "ВебСтор"',
        accountName: 'cash',
        amount: 320000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:05:00.000Z',
    },
    {
        organization: 'ООО "ВебСтор"',
        accountName: 'noncash',
        amount: 5600000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:05:00.000Z',
    },
    {
        organization: 'ИП Петров С.М.',
        accountName: 'cash',
        amount: 75000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:00:00.000Z',
    },
    {
        organization: 'ИП Петров С.М.',
        accountName: 'noncash',
        amount: 1800000,
        currency: 'RUB',
        syncedAt: '2026-03-30T21:00:00.000Z',
    },
];

export const MOCK_RESPONSE: BalancesResponse = {
    data: MOCK_BALANCES_DATA,
    updatedAt: '2026-03-30T21:30:00.000Z',
    isMock: true,
} as BalancesResponse;